import { Suspense, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Lock } from '@phosphor-icons/react'
import { useParams } from 'react-router'
import type { GranularityApproach } from '@/feature/financial-model/ecommerce/revenues/api/types'
import { SelectValue, SelectTrigger, Select, SelectItem, SelectItemWithIcon, SelectContent, Input } from '@/components/ui'
import { DirectCostsGranularity, DirectCostsApproach, UnitSettingPostRequest, Unit } from '@/feature/financial-model/ecommerce/cogs/api/types'
import { useGetDeatiledSingleFinancialModelQuery, useGetSingleFinancialModelSettingsV2Query } from '@/feature/financial-model/api'
import { Container, ScrollableRouteContainer } from '@/components/shared/Container'
import { TooltipInfo, TooltipProvider } from '@/components/ui/tooltip'
import { cn, parseInputValue } from '@/lib/utils'
import { RowHeader, Table, TableBody, TableCell, TableCellContent, TableHead, TableHeader, TableHeaderIcons, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { usePostUnitsSettings, useSuspenseUnitsSettings } from '@/feature/financial-model/ecommerce/cogs/api'
import { useFormatLocalizedNumber } from '@/lib/hooks/useLocaleFormatter'
import { EcommerceCommonHeader } from '@/components/shared/headers'
import { CardBox, CardLayoutBox } from '@/components/shared/cards/SelectCards'
import { PageBody, PageHeader } from '@/components/shared/page-sections.tsx'
import { DetailedCategories } from '@/feature/financial-model/ecommerce/cogs/components/detailed-categories.tsx'

const CogsSetupDirectCostRoute = () => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const { t } = useTranslation(`cogs`)
  const { financialModel } = useGetDeatiledSingleFinancialModelQuery(financialModelId!)
  const [rerenderKey, setRerenderKey] = useState(0)
  const { data: unitsSettings } = useSuspenseUnitsSettings(financialModelId!)
  const [selectedGranularity, setSelectedGranularity] = useState<DirectCostsGranularity | null>(unitsSettings.level_of_granularity ?? null)
  const { mutate: updateUnitsSettings } = usePostUnitsSettings(financialModelId!)

  const { data: setUpSettings } = useGetSingleFinancialModelSettingsV2Query({
    id: financialModelId!,
    module: `revenue`,
    section: `setup`
  })

  const modelGranularityApproach = useMemo(
    () => setUpSettings?.revenue.settings.find(setting => setting.type === `revenue_level_approach`)?.value as GranularityApproach,
    [setUpSettings]
  )

  const handleGranularityUpdate = useCallback(
    (value: DirectCostsGranularity) => {
      if (value === selectedGranularity) return
      const objToUpdate: UnitSettingPostRequest = {
        level_of_granularity: value,
        modeling_approach: unitsSettings.modeling_approach ?? null,
        unit_settings: unitsSettings.unit_settings
      }
      setSelectedGranularity(value)
      if (value === `by_product` || value === `detailed`) {
        objToUpdate.modeling_approach = null
        setRerenderKey(prev => prev + 1)
      }
      updateUnitsSettings(objToUpdate)
    },
    [selectedGranularity, unitsSettings.modeling_approach, unitsSettings.unit_settings, updateUnitsSettings]
  )

  const handleApproachUpdate = useCallback(
    (value: DirectCostsApproach) => {
      if (value === unitsSettings.modeling_approach) return
      const objToUpdate: UnitSettingPostRequest = {
        modeling_approach: value,
        level_of_granularity: selectedGranularity!,
        unit_settings: unitsSettings.unit_settings
      }
      updateUnitsSettings(objToUpdate)
    },
    [selectedGranularity, unitsSettings.modeling_approach, unitsSettings.unit_settings, updateUnitsSettings]
  )

  const granularity: Record<DirectCostsGranularity, string> = useMemo(
    () => ({
      aggregate: t(`cogs.set_up.direct_costs.granularity.aggregate`),
      by_product: t(`cogs.set_up.direct_costs.granularity.product`),
      detailed: t(`cogs.set_up.direct_costs.granularity.detailed`)
    }),
    [t]
  )

  const approach: Record<DirectCostsApproach, string> = useMemo(
    () => ({
      absolute_value: t(`cogs.set_up.direct_costs.approach.absolute`),
      cost_per_unit: t(`cogs.set_up.direct_costs.approach.x_unit`),
      percentage_of_revenues: t(`cogs.set_up.direct_costs.approach.x_percentage`)
    }),
    [t]
  )

  const computedGranularity = useMemo(() => {
    if (selectedGranularity === 'by_product' && modelGranularityApproach === 'order_level') {
      return 'aggregate'
    }
    return selectedGranularity
  }, [selectedGranularity, modelGranularityApproach])

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <TooltipProvider>
        <PageHeader>
          <EcommerceCommonHeader
            linkText={t(`cogs.set_up.direct_costs.header.back`)}
            path={`/dashboard/financial-model/${financialModelId}/ecommerce/cogs`}
          />
          <div className='mt-6 flex items-center'>
            <div>
              <h1 className='page-title'>
                {t(`cogs.set_up.direct_costs.header.title`)}
                {` - `}
                <span className='page-title-secondary-content'>{t(`cogs.set_up.direct_costs.header.subtitle`)}</span>
              </h1>
              <p className='page-subtitle'>
                {t(`cogs.home.header.subtitle`)} | {financialModel?.data?.name}
              </p>
            </div>
          </div>
        </PageHeader>
        <PageBody>
          <Container className='mt-6 min-h-0'>
            <CardLayoutBox>
              <CardBox variant='left'>
                <Select value={computedGranularity ?? undefined} onValueChange={handleGranularityUpdate}>
                  <div className='mb-2 flex items-center gap-2'>
                    <p>{t(`cogs.set_up.direct_costs.granularity.label`)}</p>
                    <TooltipInfo>{t(`cogs.set_up.direct_costs.granularity.tooltip`)}</TooltipInfo>
                  </div>
                  <SelectTrigger>
                    <SelectValue placeholder='Select Granularity' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='aggregate'>{granularity.aggregate}</SelectItem>
                    <SelectItemWithIcon
                      value='by_product'
                      icon={modelGranularityApproach === 'order_level' ? <Lock className='opacity-50' /> : undefined}
                      iconPosition='right'
                      disabled={modelGranularityApproach === 'order_level'}
                    >
                      {granularity.by_product}
                    </SelectItemWithIcon>
                    <SelectItem value='detailed'>{granularity.detailed}</SelectItem>
                  </SelectContent>
                </Select>
              </CardBox>
              <CardBox variant='right'>
                <Select
                  key={rerenderKey}
                  value={unitsSettings.level_of_granularity === `detailed` ? undefined : (unitsSettings.modeling_approach ?? undefined)}
                  onValueChange={handleApproachUpdate}
                  disabled={unitsSettings.level_of_granularity === `detailed`}
                >
                  <div className='mb-2 flex items-center gap-2'>
                    <p
                      className={cn(`font-medium`, {
                        'text-disabled': !computedGranularity || computedGranularity === `by_product`
                      })}
                    >
                      {t(`cogs.set_up.direct_costs.approach.label`)}
                    </p>
                    <TooltipInfo>{t(`cogs.set_up.direct_costs.approach.tooltip`)}</TooltipInfo>
                  </div>
                  <SelectTrigger disabled={!computedGranularity || computedGranularity === `by_product`}>
                    <SelectValue placeholder='Select Approach' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='absolute_value'>{approach.absolute_value}</SelectItem>
                    <SelectItemWithIcon
                      value='cost_per_unit'
                      icon={modelGranularityApproach === 'order_level' ? <Lock className='opacity-50' /> : undefined}
                      iconPosition='right'
                      disabled={modelGranularityApproach === 'order_level'}
                    >
                      {approach.cost_per_unit}
                    </SelectItemWithIcon>
                    <SelectItem value='percentage_of_revenues'>{approach.percentage_of_revenues}</SelectItem>
                  </SelectContent>
                </Select>
              </CardBox>
            </CardLayoutBox>
          </Container>
          <Configuration approach={unitsSettings.modeling_approach} granularity={computedGranularity} />
        </PageBody>
      </TooltipProvider>
    </ScrollableRouteContainer>
  )
}

const Configuration = (props: { approach?: DirectCostsApproach | null; granularity?: DirectCostsGranularity | null }) => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const { approach, granularity } = props
  const { t } = useTranslation(`cogs`)

  if (!granularity) return null

  if (granularity === `detailed`) return <DetailedCategories financialModelId={financialModelId!} />

  if (granularity === `aggregate` && !approach) return null

  if (granularity === `aggregate` && approach === `absolute_value`) return null

  if (granularity === `aggregate` && approach === `percentage_of_revenues`) return null

  return (
    <Container className='mt-6'>
      <p className='text-lg font-medium'>{t(`cogs.set_up.direct_costs.configuration.title.cost_per_unit`)}</p>
      <Suspense fallback={<Skeleton className='h-40 w-full' />}>
        <div className='mt-4'>
          <ConfigTable approach={approach} />
        </div>
      </Suspense>
    </Container>
  )
}

const ConfigTable = (props: { approach?: DirectCostsApproach | null }) => {
  const { t } = useTranslation(`cogs`)
  const { id: financialModelId } = useParams<{ id: string }>()
  const { data: unitsSettings } = useSuspenseUnitsSettings(financialModelId!)
  const { mutate: updateUnitsSettings } = usePostUnitsSettings(financialModelId!)

  const handlePercentageUpdate = useCallback(
    (unit: Unit) => {
      const objToUpdate: UnitSettingPostRequest = {
        modeling_approach: props.approach ?? (unit.modeling_approach as DirectCostsApproach),
        level_of_granularity: unitsSettings.level_of_granularity!,
        unit_settings: unitsSettings.unit_settings.map(u => (u.id === unit.id ? { ...u, percentage_of_units: unit.percentage_of_units } : u))
      }
      updateUnitsSettings(objToUpdate)
    },
    [props.approach, unitsSettings.level_of_granularity, unitsSettings.unit_settings, updateUnitsSettings]
  )

  const handleApproachUpdate = useCallback(
    (unit: Unit, approach: DirectCostsApproach) => {
      const objToUpdate: UnitSettingPostRequest = {
        modeling_approach: props.approach ?? null,
        level_of_granularity: unitsSettings.level_of_granularity!,
        unit_settings: unitsSettings.unit_settings.map(u => (u.id === unit.id ? { ...u, modeling_approach: approach } : u))
      }
      updateUnitsSettings(objToUpdate)
    },
    [props.approach, unitsSettings.level_of_granularity, unitsSettings.unit_settings, updateUnitsSettings]
  )

  const approach: Record<DirectCostsApproach, string> = useMemo(
    () => ({
      absolute_value: t(`cogs.set_up.direct_costs.approach.absolute`),
      cost_per_unit: t(`cogs.set_up.direct_costs.approach.x_unit`),
      percentage_of_revenues: t(`cogs.set_up.direct_costs.approach.x_percentage`)
    }),
    [t]
  )

  return (
    <Table>
      <TableHeader>
        <TableRow className='bg-transparent hover:bg-transparent'>
          <TableHead colSpan={1}>
            <span className='inline-flex items-center gap-2'>
              <TableHeaderIcons icon='text' />
              {t(`cogs.set_up.direct_costs.configuration.table.header.product`)}
            </span>
          </TableHead>
          {props.approach ? null : (
            <TableHead className='text-left' colSpan={1}>
              <span className='inline-flex items-center gap-2'>
                <TableHeaderIcons icon='options' />
                {t(`cogs.set_up.direct_costs.configuration.table.header.approach`)}
                <TooltipInfo>{t(`cogs.set_up.direct_costs.configuration.table.tooltip.approach`)}</TooltipInfo>
              </span>
            </TableHead>
          )}
          <TableHead className='text-left' colSpan={1}>
            <span className='inline-flex items-center gap-2'>
              {t(`cogs.set_up.direct_costs.configuration.table.header.unit`)}
              <TooltipInfo>{t(`cogs.set_up.direct_costs.configuration.table.tooltip.unit`)}</TooltipInfo>
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className='border-b border-gray-200'>
        {unitsSettings?.unit_settings.map(unit => (
          <TableRow key={unit.id} className='bg-transparent hover:bg-transparent'>
            <TableCell className={cn(`bg-gray-10`, { 'w-2/5': !props.approach })}>
              <RowHeader direction='horizontal' type='output'>
                {unit.product_name}
              </RowHeader>
            </TableCell>
            {!props.approach && (
              <TableCell className='w-1/5'>
                <TableCellContent className='w-full max-w-none justify-start'>
                  <Select
                    value={unit.modeling_approach ?? undefined}
                    onValueChange={value => {
                      handleApproachUpdate(unit, value as DirectCostsApproach)
                    }}
                  >
                    <SelectTrigger
                      className={cn(`z-10 border-none`, {
                        'text-secondary': !unit.modeling_approach
                      })}
                    >
                      <SelectValue placeholder='Select Approach' className='w-full' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='absolute_value'>{approach.absolute_value}</SelectItem>
                      <SelectItem value='cost_per_unit'>{approach.cost_per_unit}</SelectItem>
                      <SelectItem value='percentage_of_revenues'>{approach.percentage_of_revenues}</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCellContent>
              </TableCell>
            )}
            <TableCell
              className={cn(`w-1/5`, {
                'w-2/5': props.approach,
                'bg-gray-10':
                  !unit.modeling_approach || unit.modeling_approach === `absolute_value` || unit.modeling_approach === `percentage_of_revenues`,
                'bg-transparent': props.approach === `cost_per_unit`
              })}
            >
              <TableCellContent className='w-full max-w-none justify-start'>
                {unit.modeling_approach === `cost_per_unit` || props.approach === `cost_per_unit` ? (
                  <ConfigPercentageInput unit={unit} handleUpdate={handlePercentageUpdate} />
                ) : (
                  <span className='ml-2 mr-auto text-secondary'>--</span>
                )}
              </TableCellContent>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const ConfigPercentageInput = ({ unit, handleUpdate }: { unit: Unit; handleUpdate: (u: Unit) => void }) => {
  const formatNumber = useFormatLocalizedNumber({
    style: `percent`,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  })

  const [value, setValue] = useState(formatNumber(unit.percentage_of_units))

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseInputValue(e.target.value)
    setValue(parsedValue)
  }, [])

  const handleFocus = useCallback(() => {
    setValue(unit.percentage_of_units?.toString() ?? ``)
  }, [unit.percentage_of_units])

  const handleBlur = useCallback(() => {
    if (!value && !unit.percentage_of_units) return

    if (formatNumber(value) === formatNumber(unit.percentage_of_units)) return setValue(formatNumber(unit.percentage_of_units))

    handleUpdate({
      ...unit,
      percentage_of_units: value ? parseFloat(value) : null
    })
    setValue(formatNumber(value))
  }, [value, formatNumber, unit, handleUpdate])

  return (
    <Input
      placeholder='Insert percentage'
      className='table-input h-10 w-full flex-grow px-2 text-left text-sm text-secondary'
      value={value ?? ``}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  )
}

// type PreviewTab = `aggregate` | `product`

// const PreviewDialog = () => {
//   const { t: commonT } = useTranslation(`common`)
//   const { t } = useTranslation(`cogs`)
//   const [tab, setTab] = useState<PreviewTab>(`aggregate`)
//   return (
//     <Dialog>
//       <DialogTrigger asChild>
//         <Button size="md">
//           <Eye className="mr-2" size={20} />
//           {commonT(`buttons.preview`)}
//         </Button>
//       </DialogTrigger>
//       <DialogContent className="dialog-default-content">
//         <DialogHeader>
//           <DialogTitle>
//             {t(`cogs.set_up.direct_costs.preview.title`)}
//           </DialogTitle>
//           <DialogDescription>
//             {t(`cogs.set_up.direct_costs.preview.description`)}
//           </DialogDescription>
//         </DialogHeader>
//         <Tabs
//           value={tab}
//           onValueChange={(value) => setTab(value as PreviewTab)}
//         >
//           <TabsList className="bg-transparent border-none rounded-none">
//             <TabsTrigger value="aggregate" variant="underline">
//               {t(`cogs.set_up.direct_costs.preview.tabs.aggregate`)}
//             </TabsTrigger>
//             <TabsTrigger value="product" variant="underline">
//               {t(`cogs.set_up.direct_costs.preview.tabs.product`)}
//             </TabsTrigger>
//           </TabsList>
//           <TabsContent value="aggregate">
//             <StaticPreviewTable tab="aggregate" />
//           </TabsContent>
//           <TabsContent value="product">
//             <StaticPreviewTable tab="product" />
//           </TabsContent>
//         </Tabs>
//         <DialogFooter>
//           <DialogClose asChild>
//             <Button variant="secondary">{commonT(`buttons.close`)}</Button>
//           </DialogClose>
//         </DialogFooter>
//       </DialogContent>
//     </Dialog>
//   )
// }

// const StaticPreviewTable = (props: { tab: PreviewTab }) => {
//   const { t } = useTranslation(`cogs`)
//   return (
//     <div className="overflow-auto flex flex-col gap-6 w-full">
//       <Table>
//         <TableHeader>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableHead className="w-1/3">
//               <span className="inline-flex items-center gap-2 font-medium text-black">
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.header.${
//                     props.tab === `product` ? `category_1` : `absolute`
//                   }`
//                 )}
//                 {props.tab === `product` ? (
//                   <Badge variant="info">
//                     {t(
//                       `cogs.set_up.direct_costs.preview.table.labels.absolute`
//                     )}
//                   </Badge>
//                 ) : null}
//               </span>
//             </TableHead>
//             <TableHead className="w-2/3 text-center">
//               {t(`cogs.set_up.direct_costs.preview.table.header.fy`)}
//             </TableHead>
//           </TableRow>
//         </TableHeader>
//         <TableBody>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(`cogs.set_up.direct_costs.preview.table.rows.revenues`)}
//               </RowHeader>
//             </TableCell>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <TableCell className="w-2/3 bg-gray-50">
//                   <TableCellContent className="justify-end w-full text-black max-w-none">
//                     X,000 €
//                   </TableCellContent>
//                 </TableCell>
//               </TooltipTrigger>
//               <TooltipContent>
//                 {t(`cogs.set_up.direct_costs.preview.table.tooltips.revenues`)}
//               </TooltipContent>
//             </Tooltip>
//           </TableRow>
//           <TableRow className="bg-blue-50 hover:bg-blue-50 font-medium">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(`cogs.set_up.direct_costs.preview.table.rows.direct_costs`)}
//               </RowHeader>
//             </TableCell>
//             <TableCell className="w-1/3">
//               <TableCellContent className="justify-end w-full max-w-none">
//                 X,000 € (Input field)
//               </TableCellContent>
//             </TableCell>
//           </TableRow>
//         </TableBody>
//       </Table>

//       <Table>
//         <TableHeader>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableHead className="w-1/3">
//               <span className="inline-flex items-center gap-2 font-medium text-black">
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.header.${
//                     props.tab === `product` ? `category_2` : `cost_per_unit`
//                   }`
//                 )}
//                 {props.tab === `product` ? (
//                   <Badge variant="info">
//                     {t(
//                       `cogs.set_up.direct_costs.preview.table.labels.cost_per_unit`
//                     )}
//                   </Badge>
//                 ) : null}
//               </span>
//             </TableHead>
//             <TableHead className="w-2/3 text-center">
//               {t(`cogs.set_up.direct_costs.preview.table.header.fy`)}
//             </TableHead>
//           </TableRow>
//         </TableHeader>
//         <TableBody>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(`cogs.set_up.direct_costs.preview.table.rows.total_units`)}
//               </RowHeader>
//             </TableCell>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <TableCell className="w-2/3 bg-gray-50">
//                   <TableCellContent className="justify-end w-full text-black max-w-none">
//                     X00
//                   </TableCellContent>
//                 </TableCell>
//               </TooltipTrigger>
//               <TooltipContent>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.tooltips.total_units`
//                 )}
//               </TooltipContent>
//             </Tooltip>
//           </TableRow>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.rows.average_price_per_unit`
//                 )}
//               </RowHeader>
//             </TableCell>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <TableCell className="w-2/3 bg-gray-50">
//                   <TableCellContent className="justify-end w-full text-black max-w-none">
//                     X,000 €
//                   </TableCellContent>
//                 </TableCell>
//               </TooltipTrigger>
//               <TooltipContent>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.tooltips.average_price_per_unit`
//                 )}
//               </TooltipContent>
//             </Tooltip>
//           </TableRow>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(`cogs.set_up.direct_costs.preview.table.rows.cost_per_unit`)}
//               </RowHeader>
//             </TableCell>
//             <TableCell className="w-2/3">
//               <TableCellContent className="justify-end w-full text-black max-w-none">
//                 X,000 € (Input field)
//               </TableCellContent>
//             </TableCell>
//           </TableRow>
//           <TableRow className="bg-blue-50 hover:bg-blue-50 font-medium">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.rows.direct_costs_d`
//                 )}
//               </RowHeader>
//             </TableCell>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <TableCell className="w-2/3">
//                   <TableCellContent className="justify-end w-full max-w-none">
//                     X,000 €
//                   </TableCellContent>
//                 </TableCell>
//               </TooltipTrigger>
//               <TooltipContent>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.tooltips.direct_costs_derived`
//                 )}
//               </TooltipContent>
//             </Tooltip>
//           </TableRow>
//         </TableBody>
//       </Table>

//       <Table>
//         <TableHeader>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableHead className="w-1/3">
//               <span className="inline-flex items-center gap-2 font-medium text-black">
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.header.${
//                     props.tab === `product`
//                       ? `category_3`
//                       : `percentage_of_revenues`
//                   }`
//                 )}
//                 {props.tab === `product` ? (
//                   <Badge variant="info">
//                     {t(
//                       `cogs.set_up.direct_costs.preview.table.labels.percentage_of_revenues`
//                     )}
//                   </Badge>
//                 ) : null}
//               </span>
//             </TableHead>
//             <TableHead className="w-2/3 text-center">
//               {t(`cogs.set_up.direct_costs.preview.table.header.fy`)}
//             </TableHead>
//           </TableRow>
//         </TableHeader>
//         <TableBody>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(`cogs.set_up.direct_costs.preview.table.rows.revenues`)}
//               </RowHeader>
//             </TableCell>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <TableCell className="w-2/3 bg-gray-50">
//                   <TableCellContent className="justify-end w-full text-black max-w-none">
//                     X,000 €
//                   </TableCellContent>
//                 </TableCell>
//               </TooltipTrigger>
//               <TooltipContent>
//                 {t(`cogs.set_up.direct_costs.preview.table.tooltips.revenues`)}
//               </TooltipContent>
//             </Tooltip>
//           </TableRow>

//           <TableRow className="bg-blue-50 hover:bg-blue-50 font-medium">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.rows.percentage_of_revenues`
//                 )}
//               </RowHeader>
//             </TableCell>
//             <TableCell className="w-1/3">
//               <TableCellContent className="justify-end w-full max-w-none">
//                 X,000 € (Input field)
//               </TableCellContent>
//             </TableCell>
//           </TableRow>
//           <TableRow className="bg-transparent hover:bg-transparent">
//             <TableCell className="w-1/3">
//               <RowHeader>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.rows.direct_costs_c`
//                 )}
//               </RowHeader>
//             </TableCell>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <TableCell className="w-2/3 bg-gray-50">
//                   <TableCellContent className="justify-end w-full text-black max-w-none">
//                     X,000 €
//                   </TableCellContent>
//                 </TableCell>
//               </TooltipTrigger>
//               <TooltipContent>
//                 {t(
//                   `cogs.set_up.direct_costs.preview.table.tooltips.percentage_of_revenues`
//                 )}
//               </TooltipContent>
//             </Tooltip>
//           </TableRow>
//         </TableBody>
//       </Table>
//     </div>
//   )
// }

export default CogsSetupDirectCostRoute
